import { PropsWithChildren, useEffect, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import { Close, SaveOutlined } from '@mui/icons-material';
import SaveDialog from 'ui-component/dialogs/SaveDialog';
import { useDispatch } from 'store';
import { activeForm } from 'store/slices/menu';
import { LoadingButton } from '@mui/lab';

export type FormSideSectionContainerProps = {
    onCancel: () => void;
    onSubmit: () => void;
    fullHeight?: boolean;
    isEdit?: boolean;
    itemName?: string;
    customSubmit?: {
        label: string;
        onSubmit: () => void;
        icon?: any;
        disabled?: boolean;
    };
    loadingSubmit?: boolean;
    hideButtons?: boolean;
    viewOnly?: boolean;
    hideTitle?: boolean;
};

export const FormSideSectionContainer = ({
    onCancel,
    onSubmit,
    fullHeight,
    isEdit,
    itemName,
    customSubmit,
    loadingSubmit,
    hideButtons,
    viewOnly,
    children,
    hideTitle
}: PropsWithChildren<FormSideSectionContainerProps>) => {
    const dispatch = useDispatch();
    const [showConfirmation, setshowConfirmation] = useState(false);

    const {
        formState: { isValid: isFormValid },
        ...methods
    } = useFormContext();

    const SubmitIcon = customSubmit?.icon || SaveOutlined;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        dispatch(activeForm('form'));
    }, [dispatch]);

    return (
        <>
            <Box sx={{ position: 'relative', height: fullHeight ? '100%' : undefined, '& *': { textAlign: 'left' } }}>
                {!hideTitle && (
                    <Typography
                        variant="h4"
                        color="secondary"
                        textAlign="left"
                        fontWeight={500}
                        fontSize={20}
                        textTransform="capitalize"
                        sx={{
                            position: 'absolute',
                            top: '-67px',
                            zIndex: 99999
                        }}
                    >
                        {/* @ts-ignore */}
                        {isEdit ? itemName || <FormattedMessage id="edit" /> : <FormattedMessage id="new" />}
                    </Typography>
                )}

                {children}
                {/* <UsersSection selected={selectedTab === 'USERS'} /> */}

                {!hideButtons && !viewOnly && (
                    <Grid container justifyContent="flex-end" sx={{ position: 'fixed', bottom: '20px', right: '130px' }}>
                        <Button
                            disableElevation
                            disableRipple
                            sx={{
                                borderRadius: '100px',
                                bgcolor: '#F5F6F7',
                                color: '#858585',
                                mr: '12px',
                                '&:hover': { bgcolor: '#F5F6F7' }
                            }}
                            onClick={onCancel}
                            variant="contained"
                            startIcon={<Close />}
                        >
                            Cancel
                        </Button>

                        <LoadingButton
                            loading={loadingSubmit}
                            disableElevation
                            disableRipple
                            disabled={customSubmit ? customSubmit.disabled : !isFormValid}
                            color="secondary"
                            sx={{ borderRadius: '100px' }}
                            variant="contained"
                            onClick={async (e) => {
                                if (customSubmit) {
                                    customSubmit.onSubmit();
                                    return;
                                }
                                e.preventDefault();
                                if (await methods.trigger()) onSubmit();
                            }}
                            startIcon={<SubmitIcon />}
                        >
                            {customSubmit?.label || 'Save'}
                        </LoadingButton>
                    </Grid>
                )}
                {viewOnly && (
                    <Grid container justifyContent="flex-end" sx={{ position: 'fixed', bottom: '20px', right: '130px' }}>
                        <Grid item xs="auto">
                            <Button
                                disableElevation
                                disableRipple
                                sx={{
                                    borderRadius: '100px',
                                    bgcolor: '#F5F6F7',
                                    color: '#858585',
                                    mr: '12px',
                                    '&:hover': { bgcolor: '#F5F6F7' }
                                }}
                                onClick={onCancel}
                                variant="contained"
                                startIcon={<Close />}
                            >
                                Close
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Box>
            <SaveDialog
                open={showConfirmation}
                onClose={() => setshowConfirmation(false)}
                onSubmit={async () => {
                    setshowConfirmation(false);
                    onSubmit();
                }}
            />
        </>
    );
};
